import React, {useEffect, useState} from "react";
import {connect, useSelector} from "react-redux";
import {getProject, deleteProject, addProjectViewer, removeProjectViewer} from "../../actions/projects";
import {formatAddress} from "../common/Helpers";
import DocStatusTag from "../common/DocStatusTag";
import IconTrash from "../iconComponents/IconTrash";
import IconX from "../iconComponents/IconX";
import Crayon from "../iconComponents/Crayon";
import {Link} from "react-router-dom";
import ConfirmationModal from "../common/Modal/ConfirmationModal";
import AddViewerModal from "./AddViewerModal";

export function ProjectView(props) {

const project = useSelector(state => state.projects.project);
const contracts = useSelector(state => state.contract.contracts);
const noContractsExist = useSelector(state => state.contract.noContractsExist);

const [trashIsHovered, setTrashIsHovered] = useState(false);
const [editHovered, setEditHovered] = useState(false);
const [toList, setToList] = useState(false);
const [deletionModalOpen, setDeletionModalOpen] = useState(false);
const [addViewerModalOpen, setAddViewerModalOpen] = useState(false);

const render = (noContractsExist || contracts.length > 0) && project.project_id;
const viewers = project.viewers || [];

useEffect(() => {
    if (toList) {
        props.handleReroute("/mng/projects/list");
    } 
}, [project])

const activeCount = contracts.filter(contract => contract.project_id === project.project_id && contract.contract_status === "Active").length;
const draftCount = contracts.filter(contract => contract.project_id === project.project_id && contract.contract_status === "Draft").length;
const closedCount = contracts.filter(contract => contract.project_id === project.project_id && contract.contract_status === "Closed").length;
const archivedCount = 0;
//delete authority (like Viewer management) follows the project's current owner
const canDelete = activeCount + closedCount + archivedCount === 0 && project.is_owner;
const workflowCount = activeCount + draftCount + closedCount + archivedCount;

const handleDelete = (e) => {
    e.preventDefault()
    props.deleteProject(project.project_id);
    setToList(true);
}

const handleAddViewer = (email) => {
    props.addProjectViewer(project.project_id, email);
    setAddViewerModalOpen(false);
}

const handleRemoveViewer = (userId) => {
    props.removeProjectViewer(project.project_id, userId);
}

    return (
        <div className="card-wrapper">
            {render && 
            <>
                {deletionModalOpen &&
                    <ConfirmationModal handleClose={() => setDeletionModalOpen(false)} proceedButtonText="Close Project" onSubmit={handleDelete} >
                        <div className="content1 multiline light-black gap-below-m">
                            <p className="gap-below-s">Are you sure you want to close this project? You will still be able to view historic invoices and contracts related to this project, but you will ot be able to create new ones.</p>
                            <p className="heavy" >This step cannot be undone. Type "confirm" to proceed</p>
                        </div>
                    </ConfirmationModal>
                }
                {addViewerModalOpen &&
                    <AddViewerModal
                        handleClose={() => setAddViewerModalOpen(false)}
                        onSubmit={handleAddViewer}
                        ownerEmail={project.owner && project.owner.email}
                        existingViewerEmails={viewers.map(v => v.email)}
                    />
                }
                <div className="single-header space-btwn">
                    <h1 className="title heavy">Project&nbsp;<span className="light-black">{project.project_name}</span>
                        {/* GitHub request 2026-09-19: a Project previously had no way to edit its own info
                            after creation at all (ProjectEdit.js only ever created new ones) -- gated the same
                            way as delete, since ownership is this app's existing "who can manage this project" rule */}
                        {project.is_owner &&
                        <Link to={`/mng/project/edit/${project.project_id}`}>
                            <button
                                id="edit_project"
                                onMouseEnter={() => setEditHovered(true)}
                                onMouseLeave={() => setEditHovered(false)}
                            >
                                <Crayon className="iblock gap-left-xs" height="13px" pathClass={editHovered ? "g1-fill" : "g6-fill"} />
                            </button>
                        </Link>}
                    </h1>
                    {canDelete &&
                    <button
                        id="delete_project"
                        onMouseEnter={() => setTrashIsHovered(true)}
                        onMouseLeave={() => setTrashIsHovered(false)}
                        onClick={() => setDeletionModalOpen(true)}
                    >
                        <IconTrash height="22px" pathClass={trashIsHovered ? "p-fill" : "g7-fill" } className="td-icon gap-left-s"  style={{marginTop: "-2px"}} />  
                    </button>
                    }
                </div>
                <div className="contract-body flex-row gap-below-m">
                    <div className="flex-row gap-right-l">
                        <div className="flex-column">
                            <h3 className="content2 heavy gap-below-xs">Address</h3>
                            <p className="content1 light multiline gap-below-s">
                                {formatAddress([[project.street],
                                                [project.city, project.state],
                                                [project.country],
                                                [project.postal]])}
                            </p>
                        </div>
                    </div>
                    <div className="flex-column gap-right-l">
                        <h3 className="content2 heavy gap-below-xs">Project Status</h3>
                        <DocStatusTag status={project.project_status} textClassName="content1 light"/>
                    </div>

                    <div className="flex-column content1 light gap-right-l">
                        <Link to="/mng/workflows/list">
                        <h3 className="content2 link2 heavy gap-below-xs">{workflowCount}&nbsp;&nbsp;Contracts</h3>
                        </Link>
                        <h4><span className="heavy">{draftCount}&nbsp;&nbsp;</span>Draft</h4>
                        <h4><span className="heavy">{activeCount}&nbsp;&nbsp;</span>Active</h4>
                        <h4><span className="heavy">{closedCount}&nbsp;&nbsp;</span>Closed</h4>
                        <h4><span className="heavy">{archivedCount}&nbsp;&nbsp;</span>Archived</h4>
                    </div>

                    {/* A Basic-tier company is always exactly one person -- there's nobody else
                        to own the project or be granted a Viewer, so this whole section is
                        meaningless there (matches how AP/AR/Approval Workflow are hidden the
                        same way elsewhere) */}
                    {!project.company_is_basic &&
                    <>
                        <div className="flex-column gap-right-l">
                            <h3 className="content2 heavy gap-below-xs">Owner</h3>
                            <p className="content1 light">{project.owner ? `${project.owner.first_name} ${project.owner.last_name}` : "None"}</p>
                        </div>
                        <div className="flex-column content1 light" style={{flex: "1 1 0%", minWidth: "220px", whiteSpace: "normal"}}>
                            <div className="flex-row space-btwn" style={{alignItems: "center"}}>
                                <h3 className="content2 heavy gap-below-xs">Viewers</h3>
                                {project.is_owner &&
                                    <button id="add_viewer" className="btn pri iblock mini" onClick={() => setAddViewerModalOpen(true)}>Add Viewer</button>
                                }
                            </div>
                            {viewers.length === 0
                            ? <p className="content1 light-black">No Viewers added -- only people with a contract role on this project can see it.</p>
                            : viewers.map(viewer => (
                                <div key={viewer.user_id} className="flex-row space-btwn gap-below-xxs" style={{alignItems: "center"}}>
                                    <p className="content1 light">{viewer.first_name} {viewer.last_name} <span className="light-black">({viewer.email})</span></p>
                                    {project.is_owner &&
                                        <button onClick={() => handleRemoveViewer(viewer.user_id)} >
                                            <IconX height="12px" pathClass="g7-fill" />
                                        </button>
                                    }
                                </div>
                            ))}
                        </div>
                    </>
                    }
                </div>
            </>}
        </div>
    )
}

const mapStateToProps = () => ({})

export default connect(mapStateToProps, {getProject, deleteProject, addProjectViewer, removeProjectViewer})(ProjectView);